import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface CommissionResult {
  baseCommission: number;
  vatAmount: number;
  totalCommission: number;
}

export const calculateCommission = async (
  total: number, 
  userId: string,
  isManualOrder: boolean = true
): Promise<CommissionResult> => {
  // Check if user is exempt from commissions
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (userDoc.exists() && userDoc.data().commissionExempt) {
    return {
      baseCommission: 0,
      vatAmount: 0,
      totalCommission: 0
    };
  }

  // Use 7.5% for manual orders, 15% for private sales
  const commissionRate = isManualOrder ? 0.075 : 0.15;
  let baseCommission = Number((total * commissionRate).toFixed(2));
  let vatAmount = Number((baseCommission * 0.23).toFixed(2)); // 23% VAT
  let totalCommission = Number((baseCommission + vatAmount).toFixed(2));

  // Cap manual orders at €1.23 (including VAT)
  if (isManualOrder && totalCommission > 1.23) {
    totalCommission = 1.23;
    baseCommission = Number((1.23 / 1.23).toFixed(2)); // Remove VAT to get base
    vatAmount = Number((totalCommission - baseCommission).toFixed(2));
  }

  return {
    baseCommission,
    vatAmount,
    totalCommission
  };
};

export const calculateSellerAmount = async (
  total: number,
  userId: string,
  isManualOrder: boolean = true
): Promise<{
  sellerAmount: number;
  commission: CommissionResult;
}> => {
  const commission = await calculateCommission(total, userId, isManualOrder);
  const sellerAmount = Number((total - commission.totalCommission).toFixed(2));

  return {
    sellerAmount,
    commission
  };
};

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export interface CommissionTotals {
  totalCommissions: number;
  totalRefunds: number;
  totalVat: number;
  netTotal: number;
}

export const calculateCommissionTotals = (commissions: any[]): CommissionTotals => {
  const totals = commissions.reduce((acc, commission) => {
    const isRefund = commission.type === 'refund';
    const amount = isRefund ? -commission.amount : commission.amount;
    const vatAmount = isRefund ? -commission.vatAmount : commission.vatAmount;
    
    return {
      totalCommissions: acc.totalCommissions + (isRefund ? 0 : commission.amount),
      totalRefunds: acc.totalRefunds + (isRefund ? commission.amount : 0),
      totalVat: acc.totalVat + vatAmount,
      netTotal: acc.netTotal + amount
    };
  }, {
    totalCommissions: 0,
    totalRefunds: 0,
    totalVat: 0,
    netTotal: 0
  });

  return {
    totalCommissions: Number(totals.totalCommissions.toFixed(2)),
    totalRefunds: Number(totals.totalRefunds.toFixed(2)),
    totalVat: Number(totals.totalVat.toFixed(2)),
    netTotal: Number(totals.netTotal.toFixed(2))
  };
};